import React, { useState } from "react";
import { useIrAPadre } from "@/components/BotonVolver";
import { abrirVisor } from "@/lib/visor";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { DocAcciones } from "@/components/documentos/DocAcciones";
import { MatterGroups } from "@/components/MatterGroups";
import { MatterSecciones } from "@/components/MatterSecciones";
import { VincularDocsVivienda } from "@/components/VincularDocsVivienda";
import { Sheet, Input } from "@/pages/Familia";
import { formatApiErrorDetail } from "@/lib/api";

const Field = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-2.5 border-b border-[#E5DFD5]/70 last:border-0">
      <span className="text-sm font-semibold text-[#6E7A74]">{label}</span>
      <span className="text-base text-[#1C2421] text-right">{value}</span>
    </div>
  ) : null;

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-[#1C2421] mt-7 mb-3">
    <Icon size={20} className="text-[#3A6B56]" /> {children}
  </h2>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]">
      {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
    </select>
  </div>
);

export default function ViviendaDetail() {
  const { id } = useParams();
  const irA = useIrAPadre();
  const { getViviendaById, getMattersByVivienda, getDocumentsForVivienda, updateVivienda, deleteVivienda, getDocumentoBlobUrl, eliminarDocumento, restaurarDocumento } = useData();
  const viv = getViviendaById(id);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openingDoc, setOpeningDoc] = useState(null);
  const [showVincular, setShowVincular] = useState(false);
  const [confirmDelDoc, setConfirmDelDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  if (!viv) {
    return (
      <div className="space-y-4">
        <button onClick={() => irA("/viviendas")} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold"><Icons.ChevronLeft size={22} /> Volver</button>
        <p className="text-base text-[#6E7A74]">No encontramos esta vivienda.</p>
      </div>
    );
  }

  const matters = getMattersByVivienda(viv.id);
  const docs = getDocumentsForVivienda(viv.id);
  const matterDocIds = new Set(matters.flatMap((m) => m.documents || []));
  const docsSueltos = docs.filter((d) => !matterDocIds.has(d.id));
  const history = (viv.historial || []).slice().reverse();

  const openEdit = () => setEdit({
    alias: viv.alias || "", direccion: viv.direccion || "", tipo_tenencia: viv.tipo_tenencia || "propiedad",
    titulares: viv.titulares || "", referencia_catastral: viv.referencia_catastral || "",
    tipo_inmueble: viv.tipo_inmueble || "piso", uso: viv.uso || "vivienda habitual",
    municipio: viv.municipio || "", provincia: viv.provincia || "", codigo_postal: viv.codigo_postal || "",
    fecha_adquisicion: viv.fecha_adquisicion || "", aseguradora_hogar: viv.aseguradora_hogar || "",
    numero_poliza: viv.numero_poliza || "", notas: viv.notas || "", icon: "Home",
  });

  const saveEdit = async () => {
    if (!edit.alias.trim()) return toast.error("El alias no puede estar vacío.");
    setBusy(true);
    try {
      await updateVivienda(viv.id, { ...edit, alias: edit.alias.trim() });
      toast.success("Vivienda actualizada");
      setEdit(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar.");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteVivienda(viv.id);
      toast.success("Vivienda eliminada");
      irA("/viviendas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    } finally { setBusy(false); }
  };

  const openDoc = async (d) => {
    setOpeningDoc(d.id);
    try {
      abrirVisor(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir el documento.");
    } finally { setOpeningDoc(null); }
  };

  const doDeleteDoc = async () => {
    setDeletingDoc(true);
    const docToDelete = confirmDelDoc;
    try {
      await eliminarDocumento(docToDelete.id);
      setConfirmDelDoc(null);
      toast.success("Documento eliminado", {
        description: docToDelete.filename,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await restaurarDocumento(docToDelete.id);
              toast.success("Documento restaurado");
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo restaurar.");
            }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el documento.");
    } finally { setDeletingDoc(false); }
  };

  return (
    <div className="space-y-2" data-testid="vivienda-detail">
      <button data-testid="vivienda-detail-back" onClick={() => irA("/viviendas")} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]">
        <Icons.ChevronLeft size={22} /> Volver
      </button>

      <header className="aldia-fade-up flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center shrink-0">
          <Icons.Home size={30} className="text-[#3A6B56]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">{viv.alias}</h1>
          <p className="text-base text-[#6E7A74]">{viv.direccion || "Sin dirección"}</p>
        </div>
        <button data-testid="vivienda-edit-open" onClick={openEdit} className="w-11 h-11 rounded-full bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5] shrink-0">
          <Icons.Pencil size={18} className="text-[#4A5550]" />
        </button>
      </header>

      <SectionTitle icon={Icons.ClipboardList}>Datos de la vivienda</SectionTitle>
      <div className="bg-white border border-[#E5DFD5] rounded-2xl px-4 py-1">
        <Field label="Alias" value={viv.alias} />
        <Field label="Dirección" value={viv.direccion} />
        <Field label="Municipio" value={viv.municipio} />
        <Field label="Provincia" value={viv.provincia} />
        <Field label="Código postal" value={viv.codigo_postal} />
        <Field label="Tipo de inmueble" value={viv.tipo_inmueble} />
        <Field label="Tenencia" value={viv.tipo_tenencia} />
        <Field label="Uso" value={viv.uso} />
        <Field label="Titular(es)" value={viv.titulares} />
        <Field label="Referencia catastral" value={viv.referencia_catastral} />
        <Field label="Aseguradora del hogar" value={viv.aseguradora_hogar} />
        <Field label="Nº de póliza" value={viv.numero_poliza} />
        <Field label="Fecha de adquisición" value={viv.fecha_adquisicion} />
        <Field label="Notas" value={viv.notas} />
      </div>

      <SectionTitle icon={Icons.FileText}>Documentos asociados</SectionTitle>
      <button data-testid="vivienda-vincular-docs" onClick={() => setShowVincular(true)}
        className="w-full mb-2.5 min-h-[48px] rounded-xl bg-[#E8F0EC] text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#DCEAE1] flex items-center justify-center gap-2">
        <Icons.Link2 size={18} /> Vincular documentos existentes
      </button>
      {docs.length > 0 ? (
        <div className="space-y-2.5">
          {docs.map((d) => {
            const DIcon = d.type === "Imagen" ? Icons.Image : Icons.FileText;
            return (
              <div key={d.id} data-testid={`vivienda-doc-${d.id}`}
                className="w-full flex flex-wrap items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl p-3.5 hover:border-[#3A6B56] transition-colors">
                <button onClick={() => openDoc(d)} disabled={openingDoc === d.id}
                  data-testid={`vivienda-doc-open-${d.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:opacity-60">
                  <div className="w-10 h-10 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0"><DIcon size={20} className="text-[#3A6B56]" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-base text-[#1C2421] truncate">{d.filename}</p>
                    <p className="text-sm text-[#6E7A74]">{d.size_label || d.size} · {d.date_label}</p>
                  </div>
                  {openingDoc === d.id ? <Icons.Loader2 size={18} className="text-[#4A5550] animate-spin shrink-0" /> : <Icons.Eye size={18} className="text-[#4A5550] shrink-0" />}
                </button>
                <button data-testid={`vivienda-doc-delete-${d.id}`} onClick={() => setConfirmDelDoc(d)} aria-label="Eliminar documento"
                  className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center hover:bg-[#FEE2E2] shrink-0">
                  <Icons.Trash2 size={18} className="text-[#B91C1C]" />
                </button>
                <DocAcciones doc={d} testid={`vivienda-doc-acc-${d.id}`} />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4">Aún no hay documentos asociados a esta vivienda.</p>
      )}

      <SectionTitle icon={Icons.Layers}>Contratos, recibos y seguros</SectionTitle>
      <MatterSecciones matters={matters} docs={docsSueltos} scheme="vivienda" testidPrefix="vivienda-sec"
        onOpenDoc={openDoc}
        emptyText="Todavía no hay contratos, recibos ni seguros en esta vivienda." />

      {history.length > 0 && (
        <>
          <SectionTitle icon={Icons.History}>Historial</SectionTitle>
          <div className="bg-white border border-[#E5DFD5] rounded-2xl px-4 py-2">
            {history.map((h, i) => (
              <div key={i} className="py-2.5 border-b border-[#E5DFD5]/70 last:border-0">
                <p className="text-sm font-semibold text-[#6E7A74]">{h.date_label}</p>
                <p className="text-base text-[#1C2421]">{h.text}</p>
              </div>
            ))}
          </div>
        </>
      )}

      <button data-testid="vivienda-delete-open" onClick={() => setConfirmDel(true)}
        className="mt-6 w-full min-h-[52px] rounded-xl bg-[#FEF2F2] text-[#B91C1C] font-semibold text-base border border-[#FECACA] hover:bg-[#FEE2E2] transition-colors flex items-center justify-center gap-2">
        <Icons.Trash2 size={18} /> Eliminar vivienda
      </button>

      {edit && (
        <Sheet onClose={() => setEdit(null)} testid="vivienda-edit">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">Editar vivienda</h2>
          <Input label="Alias" value={edit.alias} onChange={(v) => setEdit({ ...edit, alias: v })} testid="vivienda-edit-alias" />
          <Input label="Dirección completa" value={edit.direccion} onChange={(v) => setEdit({ ...edit, direccion: v })} testid="vivienda-edit-direccion" />
          <Input label="Municipio" value={edit.municipio} onChange={(v) => setEdit({ ...edit, municipio: v })} testid="vivienda-edit-municipio" />
          <Input label="Provincia" value={edit.provincia} onChange={(v) => setEdit({ ...edit, provincia: v })} testid="vivienda-edit-provincia" />
          <Input label="Código postal" value={edit.codigo_postal} onChange={(v) => setEdit({ ...edit, codigo_postal: v })} testid="vivienda-edit-cp" />
          <Select label="Tipo de tenencia" value={edit.tipo_tenencia} onChange={(v) => setEdit({ ...edit, tipo_tenencia: v })} testid="vivienda-edit-tenencia" options={["propiedad", "alquiler", "otro"]} />
          <Select label="Tipo de inmueble" value={edit.tipo_inmueble} onChange={(v) => setEdit({ ...edit, tipo_inmueble: v })} testid="vivienda-edit-tipo" options={["piso", "chalet", "apartamento", "local", "otro"]} />
          <Select label="Uso" value={edit.uso} onChange={(v) => setEdit({ ...edit, uso: v })} testid="vivienda-edit-uso" options={["vivienda habitual", "segunda residencia", "alquiler", "otro"]} />
          <Input label="Titular(es)" value={edit.titulares} onChange={(v) => setEdit({ ...edit, titulares: v })} testid="vivienda-edit-titulares" />
          <Input label="Referencia catastral" value={edit.referencia_catastral} onChange={(v) => setEdit({ ...edit, referencia_catastral: v })} testid="vivienda-edit-catastral" />
          <Input label="Aseguradora del hogar" value={edit.aseguradora_hogar} onChange={(v) => setEdit({ ...edit, aseguradora_hogar: v })} testid="vivienda-edit-aseguradora" />
          <Input label="Nº de póliza" value={edit.numero_poliza} onChange={(v) => setEdit({ ...edit, numero_poliza: v })} testid="vivienda-edit-poliza" />
          <Input label="Fecha de adquisición" type="date" value={edit.fecha_adquisicion} onChange={(v) => setEdit({ ...edit, fecha_adquisicion: v })} testid="vivienda-edit-fecha" />
          <Input label="Notas" value={edit.notas} onChange={(v) => setEdit({ ...edit, notas: v })} testid="vivienda-edit-notas" />
          <button data-testid="vivienda-edit-save" onClick={saveEdit} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60">{busy ? "Guardando…" : "Guardar cambios"}</button>
        </Sheet>
      )}

      {confirmDel && (
        <Sheet onClose={() => setConfirmDel(false)} testid="vivienda-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar «{viv.alias}»?</h2>
          <p className="text-base text-[#6E7A74] mt-2">Se eliminará la ficha. Los documentos y asuntos no se borran: solo quedarán sin vivienda asignada.</p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDel(false)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="vivienda-confirm-delete-btn" onClick={doDelete} disabled={busy} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{busy ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}

      {showVincular && <VincularDocsVivienda vivienda={viv} onClose={() => setShowVincular(false)} />}

      {confirmDelDoc && (
        <Sheet onClose={() => setConfirmDelDoc(null)} testid="vivienda-doc-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar este documento?</h2>
          <p className="text-base text-[#6E7A74] mt-2">Se eliminará «{confirmDelDoc.filename}». Podrás recuperarlo desde la Papelera durante 30 días.</p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDelDoc(null)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="vivienda-doc-confirm-delete-btn" onClick={doDeleteDoc} disabled={deletingDoc} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{deletingDoc ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
